// @ts-nocheck
/**
 * 敵生成システム（メインエントリーポイント）
 *
 * 責務:
 * - 通常敵、強敵、ボスの生成
 * - フロア構成に応じた敵配置
 * - 深度レベルごとの事前選出プール管理
 * - スキル・パッシブの付与
 */

import { CardSelector, DEPTH_LEVELS, createSeededRandom } from './CardSelector';
import { LevelCalculator } from './LevelCalculator';
import { BossGenerator } from './BossGenerator';

/**
 * 敵生成クラス
 */
class EnemyGenerator {
  /**
   * @param {Object[]} cardPool - 全カードデータ配列
   * @param {number} seed - ダンジョンシード
   * @param {number} maxFloor - 最大フロア数
   */
  constructor(cardPool, seed, maxFloor) {
    this.cardPool = cardPool || [];
    this.seed = seed;
    this.maxFloor = maxFloor;
    this.random = createSeededRandom(seed);

    this.cardSelector = new CardSelector(cardPool, seed);
    this.levelCalculator = new LevelCalculator(maxFloor, seed);
    this.bossGenerator = new BossGenerator(cardPool, seed, maxFloor);

    // 深度レベルごとの事前選出プール（initializeDepthPoolsで初期化）
    this.depthPools = {};
  }

  /**
   * 深度レベルごとの敵プールを初期化
   * ダンジョン生成時に1度だけ呼び出す
   *
   * @param {Object} dungeon - ダンジョン情報
   */
  initializeDepthPools(dungeon) {
    const { key1, key2 } = dungeon;
    const keyAttributes = this._getKeyAttributes(key1, key2);

    this.depthPools = {};

    for (const level of DEPTH_LEVELS) {
      // このダンジョンで使用する深度レベルのみ初期化
      if (level.maxFloor < 1) continue;

      const poolKey = level.depth;
      this.depthPools[poolKey] = {
        normal: [],
        strong: [],
      };

      // 通常敵プール: 各属性ごとにpoolSize体選出
      for (const attr of keyAttributes) {
        const candidates = this.cardSelector.selectCards({
          attribute: attr,
          costRange: level.costRange,
          count: level.poolSize,
        });
        this.depthPools[poolKey].normal.push(...candidates);
      }

      // 属性がない場合のフォールバック
      if (keyAttributes.length === 0) {
        const candidates = this.cardSelector.selectCards({
          costRange: level.costRange,
          count: level.poolSize,
        });
        this.depthPools[poolKey].normal.push(...candidates);
      }

      // 強敵プール: コスト範囲を+1して選出
      const strongCostRange = {
        min: level.costRange.min + 1,
        max: Math.min(10, level.costRange.max + 1),
      };

      for (const attr of keyAttributes) {
        const candidates = this.cardSelector.selectCards({
          attribute: attr,
          costRange: strongCostRange,
          count: level.poolSize,
        });
        this.depthPools[poolKey].strong.push(...candidates);
      }

      // 属性がない場合のフォールバック
      if (keyAttributes.length === 0) {
        const candidates = this.cardSelector.selectCards({
          costRange: strongCostRange,
          count: level.poolSize,
        });
        this.depthPools[poolKey].strong.push(...candidates);
      }
    }
  }

  /**
   * 鍵カードから属性リストを取得
   *
   * @param {Object} key1 - 鍵カード1
   * @param {Object} key2 - 鍵カード2
   * @returns {string[]} 属性配列
   * @private
   */
  _getKeyAttributes(key1, key2) {
    const attributes = [];
    if (key1?.attribute) attributes.push(key1.attribute);
    if (key2?.attribute && key2.attribute !== key1?.attribute) {
      attributes.push(key2.attribute);
    }
    return attributes;
  }

  /**
   * フロアに対応する深度プールを取得
   *
   * @param {number} floorNumber - フロア番号
   * @param {string} enemyType - 'normal' | 'strong'
   * @returns {Object[]} カードプール
   * @private
   */
  _getDepthPool(floorNumber, enemyType = 'normal') {
    const depthLevel = this.cardSelector.getDepthLevel(floorNumber);
    const pool = this.depthPools[depthLevel.depth];
    if (!pool) return [];
    return pool[enemyType] || [];
  }

  /**
   * プールからランダムに1体選出（重複可能）
   *
   * @param {Object[]} pool - カードプール
   * @param {number} floorNumber - フロア番号
   * @param {number} index - 敵インデックス
   * @returns {Object|null} 選出されたカード
   * @private
   */
  _selectFromPool(pool, floorNumber, index) {
    if (pool.length === 0) return null;
    // 階層とインデックスごとに異なるシードを使用
    const floorRandom = createSeededRandom(this.seed + floorNumber * 1000 + index * 7);
    const idx = Math.floor(floorRandom() * pool.length);
    return pool[idx];
  }

  /**
   * 通常敵を生成
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {number} floorNumber - フロア番号
   * @param {number} [count=2] - 生成数
   * @returns {Object[]} 敵データ配列
   */
  generateNormalEnemies(dungeon, floorNumber, count = 2) {
    const enemies = [];
    const { enemyBias } = dungeon;

    const level = this.levelCalculator.calculateLevel(floorNumber, 'normal');
    const rarity = this.levelCalculator.getEnemyTypeRarity('normal');

    // 事前選出プールから選出（重複可能）
    const pool = this._getDepthPool(floorNumber, 'normal');

    for (let i = 0; i < count; i++) {
      const card = this._selectFromPool(pool, floorNumber, i);

      if (card) {
        enemies.push(this._createEnemyData(card, {
          level,
          rarity,
          isBoss: false,
          isStrong: false,
          floorNumber,
          index: i,
        }));
      } else {
        // プールが空の場合: ジェネリック敵
        const depthLevel = this.cardSelector.getDepthLevel(floorNumber);
        enemies.push(this._createGenericEnemy({
          attribute: enemyBias || '闇',
          level,
          rarity,
          floorNumber,
          index: i,
        }));
      }
    }

    return enemies;
  }

  /**
   * 強敵を生成
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {number} floorNumber - フロア番号
   * @param {number} [count=1] - 生成数
   * @returns {Object[]} 敵データ配列
   */
  generateStrongEnemies(dungeon, floorNumber, count = 1) {
    const enemies = [];
    const { enemyBias } = dungeon;

    const level = this.levelCalculator.calculateLevel(floorNumber, 'strong');
    const rarity = this.levelCalculator.getEnemyTypeRarity('strong');

    // 事前選出プールから選出（重複可能）
    const pool = this._getDepthPool(floorNumber, 'strong');

    for (let i = 0; i < count; i++) {
      // 強敵は通常敵と異なるシードオフセットを使用
      const card = this._selectFromPool(pool, floorNumber, i + 100);

      if (card) {
        enemies.push(this._createEnemyData(card, {
          level,
          rarity,
          isBoss: false,
          isStrong: true,
          floorNumber,
          index: i,
        }));
      }
      // 該当なしの場合は生成しない（ジェネリック敵なし）
    }

    return enemies;
  }

  /**
   * ボス遭遇を生成（ボス + 取り巻き）
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {number} floorNumber - フロア番号
   * @returns {Object} { boss, minions, allEnemies }
   */
  generateBossEncounter(dungeon, floorNumber) {
    const { boss, minions } = this.bossGenerator.generateBossEncounter(dungeon, floorNumber);

    return {
      boss,
      minions,
      allEnemies: [boss, ...minions],
    };
  }

  /**
   * フロアの敵構成を生成
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {number} floorNumber - フロア番号
   * @param {string} floorType - フロアタイプ ('normal' | 'elite' | 'boss')
   * @returns {Object[]} 敵データ配列
   */
  generateFloorEnemies(dungeon, floorNumber, floorType = 'normal') {
    switch (floorType) {
      case 'boss':
        const { allEnemies } = this.generateBossEncounter(dungeon, floorNumber);
        return allEnemies;

      case 'elite':
        return this.generateStrongEnemies(dungeon, floorNumber, 1);

      case 'normal':
      default:
        // 通常敵 2-3体
        const count = 2 + Math.floor(this.random() * 2);
        return this.generateNormalEnemies(dungeon, floorNumber, count);
    }
  }

  /**
   * 敵データを構築
   *
   * @param {Object} card - カードデータ
   * @param {Object} options - オプション
   * @returns {Object} 敵データ
   * @private
   */
  _createEnemyData(card, options) {
    const { level, rarity, isBoss, isStrong, floorNumber, index = 0 } = options;

    const enemyId = isBoss
      ? `boss_${floorNumber}`
      : `enemy_${floorNumber}_${index}`;

    return {
      id: enemyId,
      cardId: card.id,
      card: { ...card },
      name: card.name,
      attribute: card.attribute,
      category: card.category,
      level,
      rarity,
      isBoss,
      isStrong,
      baseAttack: parseInt(card.attack) || 500,
      baseHp: parseInt(card.hp) || 500,
    };
  }

  /**
   * ジェネリック敵を作成（カードが見つからない場合のフォールバック）
   *
   * @param {Object} options - オプション
   * @returns {Object} ジェネリック敵データ
   * @private
   */
  _createGenericEnemy(options) {
    const { attribute, level, rarity, floorNumber, index } = options;

    const attributeNames = {
      '炎': '炎の',
      '水': '水の',
      '光': '光の',
      '闘': '闇の',
      '原始': '原始の',
      '未来': '未来の',
    };

    const prefix = attributeNames[attribute] || '';

    return {
      id: `enemy_${floorNumber}_${index}`,
      cardId: null,
      card: null,
      name: `${prefix}モンスター`,
      attribute,
      category: null,
      level,
      rarity,
      isBoss: false,
      isStrong: false,
      baseAttack: 300 + level * 20,
      baseHp: 400 + level * 30,
    };
  }

  /**
   * 乱数をリセット
   */
  resetRandom() {
    this.random = createSeededRandom(this.seed);
    this.cardSelector.resetRandom();
  }

  /**
   * 深度プールの内容を取得（デバッグ用）
   *
   * @returns {Object} 深度プール
   */
  getDepthPools() {
    return this.depthPools;
  }
}

export default EnemyGenerator;
export { EnemyGenerator };
